import React from "react"
import { useNavigate } from "react-router-dom"

import ReservationStatusModal from "@/components/public/reservation_status_modal"
import { PublicReservationStatus } from "@/graphql/graphql"
import i18n from "@/i18n"
import reservationCancelledModalIcon from "@/pages/public/reservation_cancelled_modal_icon.svg"

import { type CancelModalState } from "./use_cancellation_flow"

type Props = {
  cancelContactHref?: string
  cancelLoading: boolean
  cancelModalState: CancelModalState
  onClose: () => void
  onConfirm: () => void
  reservationStatus?: PublicReservationStatus
  venueSlug?: string
}

const CancellationModals: React.FC<Props> = ({
  cancelContactHref,
  cancelLoading,
  cancelModalState,
  onClose,
  onConfirm,
  reservationStatus,
  venueSlug,
}) => {
  const navigate = useNavigate()

  if (!cancelModalState) return null

  if (cancelModalState === "confirm") {
    return (
      <ReservationStatusModal
        open
        onClose={onClose}
        icon={reservationCancelledModalIcon}
        title={i18n.t("app.public.reservation.modals.cancel_confirm.title")}
        description={i18n.t("app.public.reservation.modals.cancel_confirm.description")}
        primaryAction={{
          label: i18n.t("app.common.true"),
          onClick: onConfirm,
          disabled: cancelLoading,
        }}
        secondaryAction={{ label: i18n.t("app.common.false"), onClick: onClose }}
      />
    )
  }

  if (cancelModalState === "blocked") {
    return (
      <ReservationStatusModal
        open
        onClose={onClose}
        icon={reservationCancelledModalIcon}
        title={i18n.t("app.public.reservation.modals.cancel_blocked.title")}
        description={
          reservationStatus === PublicReservationStatus.Cancelled
            ? i18n.t("app.public.reservation.cancel_unavailable")
            : i18n.t("app.public.reservation.cancellation_deadline_passed")
        }
        primaryAction={{
          label: i18n.t("app.public.reservation.contact_venue"),
          href: cancelContactHref,
          onClick: onClose,
        }}
        secondaryAction={{ label: i18n.t("app.public.venue.modal.close"), onClick: onClose }}
      />
    )
  }

  return (
    <ReservationStatusModal
      open
      onClose={onClose}
      icon={reservationCancelledModalIcon}
      title={i18n.t("app.public.reservation.modals.cancel_success.title")}
      description={i18n.t("app.public.reservation.modals.cancel_success.description")}
      primaryAction={{
        label: i18n.t("app.mailers.reservation_cancelled.cta"),
        onClick: () => {
          if (!venueSlug) return

          navigate(`/v/${venueSlug}`)
        },
      }}
    />
  )
}

export default CancellationModals
